import {
  PATIENT_LIST_CLEAR_FILTERS,
  PATIENT_LIST_ERROR,
  PATIENT_LIST_RECEIVED,
  PATIENT_LIST_REQUEST,
  PATIENT_LIST_SET_FILTERS,
  PATIENT_LIST_SET_PAGE,
  PATIENT_LIST_UNLOAD,
  PATIENT_CONSULTANT_UPDATED,
  PATIENT_SINGLE_ERROR,
  PATIENT_SINGLE_RECEIVED,
  PATIENT_SINGLE_REQUEST,
  PATIENT_SINGLE_UNLOAD,
} from '../constants';

import { hydraPageCount } from '../../helper/apiUtils';
import { removeFiltersFromSession, setFiltersInSession } from '../../routing/filters';

/**
 * MED-921 - the patient index and detail.
 *
 * Follows the ccsdCodes reducer, with one deliberate difference: PATIENT_SINGLE_REQUEST clears
 * the patient it is holding. Everywhere else in this app the previous record staying on screen
 * during a fetch is a harmless flicker; on a register of real people it means showing one
 * patient's name and invoices under another patient's URL.
 */
const initialState = {
  patients: [],
  isFetchingPatients: false,
  currentPage: 1,
  pageCount: null,

  patient: null,
  isFetchingPatient: false,
  hasLoadedPatient: false,

  filters: {},
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case PATIENT_LIST_REQUEST:
      return {
        ...state,
        isFetchingPatients: true,
      };

    case PATIENT_LIST_RECEIVED:
      return {
        ...state,
        patients: action.data['hydra:member'],
        pageCount: hydraPageCount(action.data),
        isFetchingPatients: false,
      };

    case PATIENT_LIST_ERROR:
      return {
        ...state,
        error: action.error,
        isFetchingPatients: false,
      };

    case PATIENT_LIST_SET_PAGE:
      return {
        ...state,
        currentPage: action.page,
      };

    case PATIENT_LIST_SET_FILTERS:
      setFiltersInSession(action.routeKey || null, action.filters);
      return {
        ...state,
        filters: action.filters,
      };

    case PATIENT_LIST_CLEAR_FILTERS:
      removeFiltersFromSession(action.routeKey || null);
      return {
        ...state,
        filters: {},
      };

    case PATIENT_LIST_UNLOAD:
      return {
        ...state,
        patients: [],
        isFetchingPatients: false,
        currentPage: 1,
        pageCount: null,
        filters: {},
      };

    case PATIENT_SINGLE_REQUEST:
      return {
        ...state,
        patient: null,
        hasLoadedPatient: false,
        isFetchingPatient: true,
      };

    case PATIENT_SINGLE_RECEIVED:
      return {
        ...state,
        patient: action.data,
        hasLoadedPatient: true,
        isFetchingPatient: false,
      };

    case PATIENT_SINGLE_ERROR:
      return {
        ...state,
        error: action.error,
        patient: null,
        hasLoadedPatient: false,
        isFetchingPatient: false,
      };

    /**
     * MED-1002. The consultants array on a patient is a read model stamped by the API from the
     * invoice table, so a PATCH of one pair returns the pair and not the patient. Rather than
     * re-fetch the whole record for one boolean, the matching entry is updated in place.
     *
     * Matched on `patientConsultantId`, never on the consultant: a patient can legitimately have
     * one entry per consultant, and the pair id is the only thing that identifies the row that
     * actually changed.
     */
    case PATIENT_CONSULTANT_UPDATED: {
      if (!state.patient || !action.data) {
        return state;
      }

      return {
        ...state,
        patient: {
          ...state.patient,
          consultants: (state.patient.consultants || []).map((consultant) =>
            consultant.patientConsultantId === action.data.id ? { ...consultant, noShortfall: action.data.noShortfall } : consultant,
          ),
        },
      };
    }

    case PATIENT_SINGLE_UNLOAD:
      return {
        ...state,
        patient: null,
        hasLoadedPatient: false,
        isFetchingPatient: false,
        error: null,
      };

    default:
      return state;
  }
};

export default reducer;
